import { createApi } from '@reduxjs/toolkit/query/react'
import {
  FLAG_DEFAULTS,
  parseFlagSnapshot,
  type FlagKey,
  type FlagSnapshot
} from '@atbash-labs/ontology-flags'

import { PROXY_API_V1_URL, ontologyBaseQuery } from '../core'

export { parseFlagSnapshot }

export type FlagOverride = {
  key: FlagKey
  deployment: string
  enabled: boolean
  updated_by: string
  updated_at: string
}

export type PutFlagOverrideArgs = {
  key: FlagKey
  enabled: boolean
  deployment?: string
}

export type DeleteFlagOverrideArgs = {
  key: FlagKey
  deployment?: string
}

export type FeatureGroup = {
  id: string
  display_name: string
  member_flag_keys: FlagKey[]
  default_enabled: boolean
  enabled: boolean
  overridden: boolean
}

export type PutFeatureGroupArgs = {
  groupId: string
  enabled: boolean
}

export function parseFlagOverride(raw: unknown): FlagOverride | null {
  if (!raw || typeof raw !== 'object') return null
  const row = raw as Record<string, unknown>
  if (typeof row.key !== 'string' || !(row.key in FLAG_DEFAULTS)) return null
  if (typeof row.enabled !== 'boolean') return null
  return {
    key: row.key as FlagKey,
    deployment: typeof row.deployment === 'string' ? row.deployment : '',
    enabled: row.enabled,
    updated_by: typeof row.updated_by === 'string' ? row.updated_by : '',
    updated_at: typeof row.updated_at === 'string' ? row.updated_at : ''
  }
}

export function parseFlagOverrideList(raw: unknown): FlagOverride[] {
  const body = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}
  const rows = Array.isArray(body.overrides) ? body.overrides : []
  const out: FlagOverride[] = []
  for (const row of rows) {
    const parsed = parseFlagOverride(row)
    if (parsed) out.push(parsed)
  }
  return out
}

export function parseFeatureGroup(raw: unknown): FeatureGroup | null {
  if (!raw || typeof raw !== 'object') return null
  const row = raw as Record<string, unknown>
  if (typeof row.id !== 'string' || !row.id.trim()) return null
  if (typeof row.display_name !== 'string' || !row.display_name.trim()) return null
  if (!Array.isArray(row.member_flag_keys) || row.member_flag_keys.length === 0) return null
  if (
    !row.member_flag_keys.every(
      (key): key is FlagKey => typeof key === 'string' && key in FLAG_DEFAULTS
    )
  ) {
    return null
  }
  if (
    typeof row.default_enabled !== 'boolean' ||
    typeof row.enabled !== 'boolean' ||
    typeof row.overridden !== 'boolean'
  ) {
    return null
  }
  return {
    id: row.id,
    display_name: row.display_name,
    member_flag_keys: [...row.member_flag_keys],
    default_enabled: row.default_enabled,
    enabled: row.enabled,
    overridden: row.overridden
  }
}

export function parseFeatureGroupList(raw: unknown): FeatureGroup[] {
  const body = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}
  const rows = Array.isArray(body.groups) ? body.groups : []
  const out: FeatureGroup[] = []
  for (const row of rows) {
    const parsed = parseFeatureGroup(row)
    if (parsed) out.push(parsed)
  }
  return out
}

export function flagOverrideDeletePath({ key, deployment }: DeleteFlagOverrideArgs): string {
  const params = new URLSearchParams()
  if (deployment) params.set('deployment', deployment)
  const query = params.toString()
  return `${PROXY_API_V1_URL}/flags/overrides/${encodeURIComponent(key)}${query ? `?${query}` : ''}`
}

export const FLAG_API_TAG_TYPES = ['FlagSnapshot', 'FlagOverrides', 'FeatureGroups'] as const

export const flagsApi = createApi({
  reducerPath: 'flagsApi',
  baseQuery: ontologyBaseQuery,
  refetchOnFocus: true,
  refetchOnReconnect: true,
  tagTypes: FLAG_API_TAG_TYPES,
  endpoints: (builder) => ({
    getFlagSnapshot: builder.query<FlagSnapshot, void>({
      query: () => `${PROXY_API_V1_URL}/flags/snapshot`,
      transformResponse: (response: unknown) => parseFlagSnapshot(response),
      providesTags: ['FlagSnapshot']
    }),
    listFlagOverrides: builder.query<FlagOverride[], void>({
      query: () => `${PROXY_API_V1_URL}/flags/overrides`,
      transformResponse: (response: unknown) => parseFlagOverrideList(response),
      providesTags: ['FlagOverrides']
    }),
    putFlagOverride: builder.mutation<FlagOverride, PutFlagOverrideArgs>({
      query: ({ key, enabled, deployment }) => ({
        url: `${PROXY_API_V1_URL}/flags/overrides`,
        method: 'PUT',
        body: deployment ? { key, enabled, deployment } : { key, enabled }
      }),
      transformResponse: (response: unknown) => {
        const parsed = parseFlagOverride(response)
        if (!parsed) {
          throw new Error('Flag override response was not a registered key')
        }
        return parsed
      },
      invalidatesTags: ['FlagSnapshot', 'FlagOverrides']
    }),
    deleteFlagOverride: builder.mutation<void, DeleteFlagOverrideArgs>({
      query: (args) => ({
        url: flagOverrideDeletePath(args),
        method: 'DELETE'
      }),
      invalidatesTags: ['FlagSnapshot', 'FlagOverrides']
    }),
    listFeatureGroups: builder.query<FeatureGroup[], void>({
      query: () => `${PROXY_API_V1_URL}/flags/groups`,
      transformResponse: (response: unknown) => parseFeatureGroupList(response),
      providesTags: ['FeatureGroups']
    }),
    putFeatureGroup: builder.mutation<FeatureGroup, PutFeatureGroupArgs>({
      query: ({ groupId, enabled }) => ({
        url: `${PROXY_API_V1_URL}/flags/groups/${encodeURIComponent(groupId)}`,
        method: 'PUT',
        body: { enabled }
      }),
      transformResponse: (response: unknown) => {
        const parsed = parseFeatureGroup(response)
        if (!parsed) throw new Error('Feature group response was invalid')
        return parsed
      },
      invalidatesTags: ['FeatureGroups', 'FlagSnapshot', 'FlagOverrides']
    }),
    deleteFeatureGroup: builder.mutation<void, string>({
      query: (groupId) => ({
        url: `${PROXY_API_V1_URL}/flags/groups/${encodeURIComponent(groupId)}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['FeatureGroups', 'FlagSnapshot', 'FlagOverrides']
    })
  })
})

export const {
  useGetFlagSnapshotQuery,
  useListFlagOverridesQuery,
  usePutFlagOverrideMutation,
  useDeleteFlagOverrideMutation,
  useListFeatureGroupsQuery,
  usePutFeatureGroupMutation,
  useDeleteFeatureGroupMutation
} = flagsApi
